import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

export type OccupancyPoint = {
  date: string;
  percent: number;
};

type OccupancyTargetChartProps = {
  points: OccupancyPoint[];
  onChange: (points: OccupancyPoint[]) => void;
  disabled?: boolean;
};

const WIDTH = 720;
const HEIGHT = 260;
const PAD = { top: 16, right: 16, bottom: 36, left: 40 };

function clampPercent(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function catmullRomPath(coords: { x: number; y: number }[]): string {
  const first = coords[0];
  if (!first) return "";
  if (coords.length === 1) return `M ${first.x} ${first.y}`;
  let d = `M ${first.x} ${first.y}`;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const p1 = coords[i];
    const p2 = coords[i + 1];
    if (!p1 || !p2) continue;
    const p0 = coords[i - 1] ?? p1;
    const p3 = coords[i + 2] ?? p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function formatDayLabel(iso: string, index: number, total: number): string {
  const date = new Date(`${iso}T12:00:00`);
  const day = date.getDate();
  const month = date.toLocaleDateString("de-DE", { month: "short" });
  if (total <= 14) return `${day}.${month}`;
  if (total <= 45) {
    if (index === 0 || index === total - 1 || day === 1 || index % 5 === 0) {
      return `${day}.${month}`;
    }
    return "";
  }
  if (index === 0 || index === total - 1 || day === 1 || index % 10 === 0) {
    return `${day}.${month}`;
  }
  return "";
}

export function OccupancyTargetChart({ points, onChange, disabled = false }: OccupancyTargetChartProps) {
  const gradientId = useId();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const plotW = WIDTH - PAD.left - PAD.right;
  const plotH = HEIGHT - PAD.top - PAD.bottom;

  const coords = useMemo(() => {
    if (points.length === 0) return [];
    return points.map((point, index) => {
      const x =
        points.length === 1
          ? PAD.left + plotW / 2
          : PAD.left + (index / (points.length - 1)) * plotW;
      const y = PAD.top + ((100 - point.percent) / 100) * plotH;
      return { x, y };
    });
  }, [points, plotH, plotW]);

  const linePath = useMemo(() => catmullRomPath(coords), [coords]);
  const areaPath = useMemo(() => {
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (!first || !last) return "";
    const baseY = PAD.top + plotH;
    return `${linePath} L ${last.x} ${baseY} L ${first.x} ${baseY} Z`;
  }, [coords, linePath, plotH]);

  const updatePercentAt = useCallback(
    (index: number, clientY: number) => {
      const svg = svgRef.current;
      if (!svg || disabled) return;
      const rect = svg.getBoundingClientRect();
      const scaleY = HEIGHT / rect.height;
      const localY = (clientY - rect.top) * scaleY;
      const relative = (localY - PAD.top) / plotH;
      const percent = clampPercent(100 - relative * 100);
      onChange(
        points.map((point, i) => (i === index ? { ...point, percent } : point)),
      );
    },
    [disabled, onChange, plotH, points],
  );

  useEffect(() => {
    if (dragIndex == null) return;
    const activeDragIndex = dragIndex;

    function onMove(event: PointerEvent) {
      updatePercentAt(activeDragIndex, event.clientY);
    }
    function onUp() {
      setDragIndex(null);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragIndex, updatePercentAt]);

  if (points.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-separator bg-fill-tertiary/40 px-4 py-8 text-center text-sm text-label-secondary">
        Zeitraum wählen, um den Auslastungsgraphen anzuzeigen.
      </p>
    );
  }

  const activeIndex = dragIndex ?? hoverIndex;
  const activePoint = activeIndex != null ? points[activeIndex] : null;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm text-label-secondary">
          Punkte vertikal ziehen (0–100&nbsp;%). Eine Linie = Ziel-Auslastung je Tag.
        </p>
        {activePoint ? (
          <p className="shrink-0 text-sm font-medium text-label">
            {activePoint.date}: {activePoint.percent}&nbsp;%
          </p>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-xl border border-separator bg-card">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="h-[240px] w-full min-w-[520px] touch-none select-none"
          role="img"
          aria-label="Ziel-Auslastung je Tag"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--eh-accent)" stopOpacity="0.35" />
              <stop offset="100%" stopColor="var(--eh-accent)" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {[0, 25, 50, 75, 100].map((tick) => {
            const y = PAD.top + ((100 - tick) / 100) * plotH;
            return (
              <g key={tick}>
                <line
                  x1={PAD.left}
                  x2={WIDTH - PAD.right}
                  y1={y}
                  y2={y}
                  stroke="var(--eh-separator, #d1d5db)"
                  strokeWidth={tick === 0 ? 1.25 : 1}
                  strokeDasharray={tick === 0 || tick === 100 ? undefined : "3 4"}
                />
                <text
                  x={PAD.left - 8}
                  y={y + 4}
                  textAnchor="end"
                  className="fill-current text-[11px] text-label-tertiary"
                >
                  {tick}%
                </text>
              </g>
            );
          })}

          {points.map((point, index) => {
            const x = coords[index]?.x ?? 0;
            const label = formatDayLabel(point.date, index, points.length);
            return (
              <g key={`grid-${point.date}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={PAD.top}
                  y2={PAD.top + plotH}
                  stroke="var(--eh-separator, #e5e7eb)"
                  strokeWidth={1}
                  opacity={0.45}
                />
                {label ? (
                  <text
                    x={x}
                    y={HEIGHT - 12}
                    textAnchor="middle"
                    className="fill-current text-[10px] text-label-tertiary"
                  >
                    {label}
                  </text>
                ) : null}
              </g>
            );
          })}

          {areaPath ? <path d={areaPath} fill={`url(#${gradientId})`} /> : null}
          {linePath ? (
            <path
              d={linePath}
              fill="none"
              stroke="var(--eh-accent)"
              strokeWidth={2.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ) : null}

          {coords.map((coord, index) => {
            const point = points[index];
            if (!point) return null;
            const active = index === activeIndex;
            return (
              <circle
                key={point.date}
                cx={coord.x}
                cy={coord.y}
                r={active ? 7 : 5.5}
                fill="var(--eh-card, #fff)"
                stroke="var(--eh-accent)"
                strokeWidth={active ? 3 : 2.25}
                className={disabled ? "cursor-default opacity-70" : "cursor-ns-resize"}
                onPointerDown={(event) => {
                  if (disabled) return;
                  event.currentTarget.setPointerCapture?.(event.pointerId);
                  setDragIndex(index);
                  updatePercentAt(index, event.clientY);
                }}
                onPointerEnter={() => setHoverIndex(index)}
                onPointerLeave={() => setHoverIndex((current) => (current === index ? null : current))}
              >
                <title>
                  {point.date}: {point.percent}%
                </title>
              </circle>
            );
          })}
        </svg>
      </div>
    </div>
  );
}
